import React, { useEffect, useState } from "react";
import { useSOC } from "../context/SOCContext";
import { DataTable } from "../../common/DataTable";
import Drawer, { DrawerSection, Timeline } from "../ui/Drawer";
import { SeverityBadge, StatusBadge } from "../ui/Badges";
import ApiLoader from "../../ui/ApiLoader";
import { ALERT_STATUSES } from "../data/mockData";

const Alerts = ({ focusId, onConsumeFocus, onNavigate }) => {
  const {
    alerts, updateAlertStatus, markAlertRead, escalateToIncident, openInvestigation,
    events, incidents, loading, errors,
  } = useSOC();
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");

  const selectAlert = (alert) => {
    setSelected(alert);
    if (alert && !alert.isRead) markAlertRead(alert.id);
  };

  useEffect(() => {
    if (focusId) {
      const alert = alerts.find((a) => a.id === focusId);
      if (alert) setSelected(alert);
      onConsumeFocus && onConsumeFocus();
    }
    // eslint-disable-next-line
  }, [focusId, alerts]);

  // Keep the drawer's alert object fresh as state updates underneath it.
  useEffect(() => {
    if (selected) {
      const fresh = alerts.find((a) => a.id === selected.id);
      if (fresh && fresh !== selected) setSelected(fresh);
    }
    // eslint-disable-next-line
  }, [alerts]);

  const relatedEvent = selected ? events.find((e) => e.id === selected.relatedEventId) : null;
  const relatedIncident = selected ? incidents.find((i) => i.id === selected.relatedIncidentId) : null;

  const uniqueSources = [...new Set(alerts.map((a) => a.source).filter(Boolean))];

  const columns = [
    {
      id: "id",
      accessorFn: (r) => r.id,
      header: "Alert ID",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Alert ID", placeholder: "Search alert ID" } },
      cell: ({ row }) => <span>{row.original.id}</span>,
    },
    {
      id: "name",
      accessorFn: (r) => r.name,
      header: "Alert Name",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Alert Name", placeholder: "Search alert name" } },
      cell: ({ row }) => <span>{row.original.name}</span>,
    },
    {
      id: "severity",
      accessorFn: (r) => r.severity,
      header: "Severity",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Severity",
          placeholder: "All severities",
          options: ["critical", "high", "medium", "low"].map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <SeverityBadge severity={row.original.severity} />,
    },
    {
      id: "source",
      accessorFn: (r) => r.source,
      header: "Source",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Source",
          placeholder: "All sources",
          options: uniqueSources.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{row.original.source}</span>,
    },
    {
      id: "sourceIp",
      accessorFn: (r) => r.sourceIp,
      header: "Source IP",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Source IP", placeholder: "Search source IP" } },
      cell: ({ row }) => <span>{row.original.sourceIp}</span>,
    },
    {
      id: "destination",
      accessorFn: (r) => r.destination,
      header: "Destination",
      enableSorting: false,
      cell: ({ row }) => <span>{row.original.destination}</span>,
    },
    {
      id: "user",
      accessorFn: (r) => r.user,
      header: "User",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "User", placeholder: "Search user" } },
      cell: ({ row }) => <span>{row.original.user}</span>,
    },
    {
      id: "assetName",
      accessorFn: (r) => r.assetName,
      header: "Asset",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Asset", placeholder: "Search asset" } },
      cell: ({ row }) => <span>{row.original.assetName}</span>,
    },
    {
      id: "detectionTime",
      accessorFn: (r) => r.detectionTime,
      header: "Detection Time",
      enableSorting: false,
      cell: ({ row }) => <span>{new Date(row.original.detectionTime).toLocaleString()}</span>,
    },
    {
      id: "status",
      accessorFn: (r) => r.status,
      header: "Status",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Status",
          placeholder: "All statuses",
          options: ALERT_STATUSES.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      id: "analyst",
      accessorFn: (r) => r.analyst,
      header: "Assigned Analyst",
      enableSorting: false,
      cell: ({ row }) => row.original.analyst || <span className="text-gray-400">Unassigned</span>,
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({ row }) => (
        <button
          onClick={() => selectAlert(row.original)}
          className="text-blue-600 hover:underline text-xs font-medium"
        >
          View
        </button>
      ),
    },
  ];

  if (loading.alerts && alerts.length === 0) {
    return <ApiLoader variant="spinner" label="Loading Alerts..." className="py-20" />;
  }

  return (
    <div>
      <DataTable
        columns={columns}
        data={alerts}
        getRowId={(r) => String(r.id)}
        emptyMessage={errors.alerts || "No records found"}
        enableColumnVisibility={false}
        enableColumnResizing
        enableColumnFilters
        maxBodyHeight="65vh"
      />

      <Drawer
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.name}
        subtitle={selected?.id}
      >
        {selected && (
          <>
            <DrawerSection label="Overview">
              <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                <span className="text-gray-400">Severity</span>
                <SeverityBadge severity={selected.severity} />
                <span className="text-gray-400">Status</span>
                <StatusBadge status={selected.status} />
                <span className="text-gray-400">Detection Time</span>
                <span>{new Date(selected.detectionTime).toLocaleString()}</span>
                <span className="text-gray-400">Source</span>
                <span>{selected.source}</span>
                <span className="text-gray-400">Source IP</span>
                <span>{selected.sourceIp}</span>
                <span className="text-gray-400">Destination</span>
                <span>{selected.destination}</span>
                <span className="text-gray-400">User</span>
                <span>{selected.user}</span>
                <span className="text-gray-400">Asset</span>
                <span>{selected.assetName}</span>
                <span className="text-gray-400">MITRE ATT&amp;CK</span>
                <span>{selected.mitre}</span>
              </div>
            </DrawerSection>

            <DrawerSection label="Description">
              <p>{selected.description}</p>
            </DrawerSection>

            <DrawerSection label="Detection Reason">
              <p>{selected.detectionReason}</p>
            </DrawerSection>

            {relatedEvent && (
              <DrawerSection label="Related Event">
                <p className="text-gray-500">
                  {relatedEvent.type} on {relatedEvent.assetName} at {new Date(relatedEvent.timestamp).toLocaleString()}
                </p>
              </DrawerSection>
            )}

            <DrawerSection label="Investigation & Incident">
              <div className="flex flex-wrap gap-2">
                {relatedIncident ? (
                  <button
                    onClick={() => onNavigate("incidents", relatedIncident.id)}
                    className="px-3 py-1.5 text-xs rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
                  >
                    View Incident {relatedIncident.id}
                  </button>
                ) : (
                  <button
                    onClick={() => escalateToIncident(selected)}
                    className="px-3 py-1.5 text-xs rounded-lg bg-orange-600/20 text-orange-400 border border-orange-600/30 hover:bg-orange-600/30"
                  >
                    Escalate to Incident
                  </button>
                )}
                <button
                  onClick={async () => {
                    const inc = relatedIncident || (await escalateToIncident(selected));
                    if (inc) openInvestigation(inc, selected);
                  }}
                  className="px-3 py-1.5 text-xs rounded-lg bg-purple-600/20 text-purple-400 border border-purple-600/30 hover:bg-purple-600/30"
                >
                  Open Investigation
                </button>
              </div>
            </DrawerSection>

            <DrawerSection label="Update Status">
              <div className="flex flex-wrap gap-2">
                {ALERT_STATUSES.map((s) => (
                  <button
                    key={s}
                    onClick={() => updateAlertStatus(selected.id, s, selected.analyst || "A. Rahman")}
                    className={`px-3 py-1.5 text-xs rounded-lg border ${
                      selected.status === s
                        ? "bg-blue-100 border-blue-300 text-blue-700"
                        : "border-gray-200 text-gray-500 hover:bg-gray-100"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </DrawerSection>

            <DrawerSection label="Timeline">
              <Timeline items={selected.timeline} />
            </DrawerSection>

            <DrawerSection label="Analyst Notes">
              <div className="space-y-2 mb-2">
                {selected.notes.length === 0 && <p className="text-gray-400">No notes yet.</p>}
                {selected.notes.map((n, i) => (
                  <p key={i} className="text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-2">
                    {n}
                  </p>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add a note..."
                  className="flex-1 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700"
                />
                <button
                  onClick={() => {
                    if (!note.trim()) return;
                    selected.notes.push(note.trim());
                    setNote("");
                    setSelected({ ...selected });
                  }}
                  className="px-3 py-1.5 text-xs rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                  Add
                </button>
              </div>
            </DrawerSection>
          </>
        )}
      </Drawer>
    </div>
  );
};

export default Alerts;
